(function () {
  'use strict';

  var STAGE1_TOTAL = 36;
  var STAGE2_TOTAL = 147;
  var TOTAL         = STAGE2_TOTAL; // all rotation/wrap math runs in the full-resolution space
  var PX_PER_FRAME  = 4;            // matches the original full-resolution drag sensitivity
  var FRICTION      = 0.92;
  var INERTIA_THRESHOLD = 0.2;
  var INERTIA_STOP  = 0.03;
  var VEL_BUFFER_MS = 80;
  var INTRO_HALF    = Math.round(TOTAL * 150 / 360); // ~61 frames = 150°
  var INTRO_MS      = 1680;
  var PRELOAD_ROOT_MARGIN = '200px 0px 200px 0px';

  // Cap expressed in degrees/ms so it stays correct regardless of frame
  // count. The round-1 fix (1 frame per 16ms) was calibrated for 36
  // frames (10°/frame each); applied unchanged at 147 frames (2.45°/frame
  // each) it would cap the spin at ~1/4 the angular speed and read as
  // artificially slow. 0.625°/ms reproduces the round-1 cap's actual
  // angular speed, now expressed independent of TOTAL.
  var MAX_DEGREES_PER_MS = 0.625;
  var MAX_FRAMES_PER_MS  = MAX_DEGREES_PER_MS * TOTAL / 360;

  function pad2(n) {
    return ('0' + n).slice(-2);
  }

  function pad3(n) {
    return ('00' + n).slice(-3);
  }

  function init(container) {
    var img = container.querySelector('[data-bs360-img]');
    if (!img) return;

    var baseUrl = container.dataset.baseUrl;
    if (!baseUrl) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function stage1Url(k) {
      return baseUrl + 'bs-bag36-' + pad2(k) + '.webp';
    }

    function stage2Url(p) {
      return baseUrl + 'bs-bag147-' + pad3(p) + '.webp';
    }

    // Maps a full-resolution position (1..147) to the nearest of the 36
    // sparse stage-1 frames. Inverse of the sampling formula used to
    // generate the 36-set from the original 147 (source_index =
    // round(i * 147/36) for i = 0..35) — keeps the displayed angle
    // consistent across stages instead of drifting.
    function stage1IndexForPosition(p) {
      var j = p - 1;
      var i = Math.round(j * STAGE1_TOTAL / STAGE2_TOTAL);
      i = ((i % STAGE1_TOTAL) + STAGE1_TOTAL) % STAGE1_TOTAL;
      return i + 1;
    }

    function wrapFrame(n) {
      return ((n - 1) % TOTAL + TOTAL) % TOTAL + 1;
    }

    // data-bs-interactive="false" (homepage instance): plays the intro
    // sweep once and then sits static — no drag, no slider, no cursor
    // suggesting interactivity. Absent or any other value = interactive
    // (PDP default).
    var interactive = container.dataset.bsInteractive !== 'false';

    // Non-interactive instances never rotate past the intro's arc, so
    // they never need more than the stage-1 frames that arc actually
    // touches — loading the full 36-frame set (let alone stage 2) would
    // be pure waste. stage1IndexForPosition is monotonic in p, so the
    // highest index touched during the 1..INTRO_HALF sweep is simply
    // the index at p = INTRO_HALF.
    var stage1LoadCount = interactive ? STAGE1_TOTAL : stage1IndexForPosition(INTRO_HALF);

    // ── State ─────────────────────────────────────────────────────────────────

    var currentFrame   = 1;
    var dragStartX      = null;
    var dragStartFrame  = 1;
    var inertiaId        = null;
    var introRaf          = null;
    var stage1Ready        = false;
    var stage2Loaded       = {}; // position (1..147) -> true once its own file has loaded

    // ── Velocity buffer ───────────────────────────────────────────────────────

    var velBuf = [];

    function trackVel(x) {
      var t = performance.now();
      velBuf.push({ t: t, x: x });
      var cut = t - VEL_BUFFER_MS;
      while (velBuf.length > 1 && velBuf[0].t < cut) velBuf.shift();
    }

    function getVel() {
      if (velBuf.length < 2) return 0;
      var a = velBuf[0], b = velBuf[velBuf.length - 1];
      var dt = b.t - a.t;
      return dt < 5 ? 0 : (b.x - a.x) / dt;
    }

    // ── Frame display ─────────────────────────────────────────────────────────
    //
    // Fallback: fehlt eine WebP-Datei, bleibt das zuletzt geladene Frame
    // stehen statt eines kaputten Bildes.

    var lastGoodSrc = img.src;

    img.addEventListener('load', function () {
      lastGoodSrc = img.src;
    });

    img.addEventListener('error', function () {
      if (img.src !== lastGoodSrc) img.src = lastGoodSrc;
    });

    function frameUrlForPosition(p) {
      if (stage2Loaded[p]) return stage2Url(p);
      return stage1Url(stage1IndexForPosition(p));
    }

    function setFrame(n) {
      var f = wrapFrame(n);
      currentFrame = f;
      img.src = frameUrlForPosition(f);
      syncSlider();
    }

    // ── Slider (fill-only progress overlay, nested inside the container) ────
    // No handle — sliderRoot (the track-area) is itself the draggable,
    // focusable, ARIA-slider hit area. sliderRoot is still cached here for
    // event-listener attachment (stable, unchanging target) — but the fill
    // element is deliberately NOT cached. It's re-queried fresh, directly
    // from `container`, on every syncSlider() call, exactly like
    // sliderOverlay already was — so a fill lookup can never silently fail
    // just because it went through a stale/null sliderRoot reference.

    var sliderRoot    = container.querySelector('[data-bs360-slider]');
    var sliderOverlay = container.querySelector('[data-bs360-slider-overlay]');

    function syncSlider() {
      var pct = (currentFrame - 1) / (TOTAL - 1) * 100;

      var fill = container.querySelector('[data-bs360-slider-fill]');
      if (fill) {
        fill.style.width = pct + '%';
      } else {
        console.warn('[bs-bag-360] .bs-360-slider__fill ([data-bs360-slider-fill]) not found inside viewer container — progress fill cannot be drawn.', container);
      }

      if (sliderRoot) sliderRoot.setAttribute('aria-valuenow', String(currentFrame));
    }

    // Indicator visibility: hidden by default, shown while the bag is
    // actively rotating (drag, slider, or keyboard), hidden again shortly
    // after motion stops. Reduced-motion's hard vs. animated toggle is
    // handled entirely in CSS (transition vs. none) — this stays the same
    // either way, it only ever adds/removes the class.
    var indicatorHideTimer = null;
    var indicatorFocused   = false;
    var INDICATOR_HIDE_DELAY = 1200;

    function showIndicator() {
      if (!sliderOverlay) return;
      clearTimeout(indicatorHideTimer);
      indicatorHideTimer = null;
      sliderOverlay.classList.add('bs-360-slider--visible');
    }

    function scheduleHideIndicator() {
      if (!sliderOverlay) return;
      clearTimeout(indicatorHideTimer);
      if (indicatorFocused) return;
      indicatorHideTimer = setTimeout(function () {
        sliderOverlay.classList.remove('bs-360-slider--visible');
      }, INDICATOR_HIDE_DELAY);
    }

    // ── Stage 1 preload (~1MB, 36 frames) ────────────────────────────────────

    var progressFill = container.querySelector('[data-bs360-progress-fill]');
    var stage1LoadedCount = 0;

    function updateStage1Progress() {
      if (!progressFill) return;
      stage1LoadedCount++;
      var pct = Math.min(100, Math.round((stage1LoadedCount / (stage1LoadCount - 1)) * 100));
      progressFill.style.width = pct + '%';
    }

    var stage1LoadTriggered = false;

    function preloadStage1() {
      if (stage1LoadTriggered) return;
      stage1LoadTriggered = true;
      var remaining = stage1LoadCount - 1; // frame 1 is already in img.src
      var completedCount = 0;
      for (var i = 2; i <= stage1LoadCount; i++) {
        (function (n) {
          var pi = new Image();
          pi.onload = pi.onerror = function () {
            updateStage1Progress();
            completedCount++;
            if (completedCount >= remaining) onStage1Complete();
          };
          pi.src = stage1Url(n);
        })(i);
      }
    }

    function onStage1Complete() {
      stage1Ready = true;
      maybeStartIntro();
      // Stage 2 (full 147-frame smoothness) only matters for drag —
      // pointless to fetch on a non-interactive instance that only ever
      // shows the intro arc.
      if (interactive) {
        setTimeout(preloadStage2, 100);
      }
    }

    // ── Stage 2 preload (background, after stage 1 — ~3.3MB, 147 frames) ────
    //
    // Runs after stage 1 finishes so it never competes with initial page
    // load or with stage 1's own bandwidth. The viewer stays usable the
    // whole time: any position not yet in stage2Loaded falls back to its
    // nearest stage-1 frame via frameUrlForPosition.

    function preloadStage2() {
      var completedCount = 0;
      for (var p = 1; p <= STAGE2_TOTAL; p++) {
        (function (position) {
          var pi = new Image();
          pi.onload = pi.onerror = function () {
            stage2Loaded[position] = true;
            completedCount++;
          };
          pi.src = stage2Url(position);
        })(p);
      }
    }

    // ── Inertia ───────────────────────────────────────────────────────────────

    function cancelInertia() {
      if (inertiaId !== null) { cancelAnimationFrame(inertiaId); inertiaId = null; }
    }

    function startInertia(velPxMs) {
      var vel = velPxMs / PX_PER_FRAME;
      vel = Math.max(-MAX_FRAMES_PER_MS, Math.min(MAX_FRAMES_PER_MS, vel));
      var driftPos = currentFrame;
      var lastT = performance.now();
      showIndicator();

      function step(now) {
        var dt = Math.min(now - lastT, 64);
        lastT = now;
        vel *= Math.pow(FRICTION, dt / 16.67);
        if (Math.abs(vel * PX_PER_FRAME) < INERTIA_STOP) {
          inertiaId = null;
          scheduleHideIndicator();
          return;
        }
        driftPos += vel * dt;
        setFrame(Math.round(driftPos));
        inertiaId = requestAnimationFrame(step);
      }

      inertiaId = requestAnimationFrame(step);
    }

    // ── Drag-end ──────────────────────────────────────────────────────────────

    function onDragEnd() {
      if (dragStartX === null) return;
      var vel = getVel();
      dragStartX = null;
      velBuf = [];
      if (reducedMotion || Math.abs(vel) < INERTIA_THRESHOLD) {
        scheduleHideIndicator();
        return;
      }
      startInertia(vel);
    }

    // ── Hint ─────────────────────────────────────────────────────────────────

    var hint      = container.querySelector('[data-bs360-hint]');
    var hintShown = false;
    var hintTimer = null;

    function hideHint() {
      if (!hint || hintShown) return;
      clearTimeout(hintTimer);
      hint.classList.remove('bs-bag__hint--visible');
      hintShown = true;
    }

    function showHint() {
      if (!hint || hintShown) return;
      hint.classList.add('bs-bag__hint--visible');
      hintTimer = setTimeout(hideHint, 3500);
    }

    // ── Intro animation ───────────────────────────────────────────────────────

    function cancelIntro() {
      if (introRaf !== null) { cancelAnimationFrame(introRaf); introRaf = null; }
    }

    function easeInOut(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function runIntro() {
      if (interactive) showHint();

      if (reducedMotion) {
        return;
      }

      var startTs = null;

      function introStep(ts) {
        if (!startTs) startTs = ts;
        var progress = Math.min((ts - startTs) / INTRO_MS, 1);
        var frame = Math.round(easeInOut(progress) * (INTRO_HALF - 1)) + 1;
        setFrame(frame);

        if (progress < 1) {
          introRaf = requestAnimationFrame(introStep);
        } else {
          introRaf = null;
        }
      }

      introRaf = requestAnimationFrame(introStep);
    }

    // ── Load trigger: IntersectionObserver only, ~200px vor Sichtbarkeit ────
    //
    // Stage 1 no longer triggers on first drag/touch — the viewer isn't
    // interactive until stage 1 finishes anyway (see the stage1Ready gate
    // on the mouse/touch handlers below), so gating the load on interaction
    // would just delay usability further.

    if ('IntersectionObserver' in window) {
      var preloadIo = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          preloadIo.disconnect();
          preloadStage1();
        }
      }, { rootMargin: PRELOAD_ROOT_MARGIN, threshold: 0 });
      preloadIo.observe(container);
    } else {
      preloadStage1();
    }

    // ── Intro trigger: bei 40 % Viewport starten, sobald Stufe 1 fertig ist ─

    var inViewport = false;
    var introFired = false;

    function maybeStartIntro() {
      if (introFired) return;
      if (!inViewport) return;
      if (!stage1Ready) return;
      introFired = true;
      runIntro();
    }

    if ('IntersectionObserver' in window) {
      var introIo = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio >= 0.4 && !inViewport) {
          introIo.disconnect();
          inViewport = true;
          maybeStartIntro();
        }
      }, { threshold: [0.4] });
      introIo.observe(container);
    } else {
      inViewport = true;
    }

    // ── Mouse + Touch — interactive instances only (PDP). A non-interactive
    // instance (data-bs-interactive="false", homepage) never attaches any
    // of this: no drag listeners at all, per spec, not just a disabled
    // state — the element plays its intro sweep and then sits static.
    //
    // Gated on stage1Ready: the viewer isn't operable until its stage-1
    // frames finish loading — dragging before that is a no-op rather than
    // fetching frames ad hoc.

    if (interactive) {
      container.addEventListener('mousedown', function (e) {
        if (!stage1Ready) return;
        e.preventDefault();
        cancelIntro();
        cancelInertia();
        hideHint();
        showIndicator();
        velBuf = [];
        dragStartX = e.clientX;
        dragStartFrame = currentFrame;
      });

      window.addEventListener('mousemove', function (e) {
        if (dragStartX === null) return;
        trackVel(e.clientX);
        setFrame(dragStartFrame + Math.round((e.clientX - dragStartX) / PX_PER_FRAME));
      });

      window.addEventListener('mouseup', onDragEnd);
    }

    var touchStartX    = null;
    var touchStartY    = null;
    var intentDetected = false;

    if (interactive) {
      container.addEventListener('touchstart', function (e) {
        if (!stage1Ready) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        intentDetected = false;
        dragStartX = null;
      }, { passive: true });

      container.addEventListener('touchmove', function (e) {
        if (touchStartX === null) return;
        var x = e.touches[0].clientX;
        var y = e.touches[0].clientY;

        if (!intentDetected) {
          var dx = x - touchStartX;
          var dy = y - touchStartY;
          if (Math.abs(dx) <= Math.abs(dy) * 1.5) return;
          intentDetected = true;
          cancelIntro();
          cancelInertia();
          hideHint();
          showIndicator();
          velBuf = [];
          dragStartX = x;
          dragStartFrame = currentFrame;
        }

        e.preventDefault();
        trackVel(x);
        setFrame(dragStartFrame + Math.round((x - dragStartX) / PX_PER_FRAME));
      }, { passive: false });

      container.addEventListener('touchend', function () {
        touchStartX = null;
        touchStartY = null;
        onDragEnd();
      });

      container.addEventListener('touchcancel', function () {
        touchStartX = null;
        touchStartY = null;
        dragStartX  = null;
        velBuf = [];
        scheduleHideIndicator();
      });
    }

    // ── Slider interaction ───────────────────────────────────────────────────
    // No handle to target separately — sliderRoot (the track-area) is the
    // whole hit area, drag surface, and focusable ARIA-slider element.

    if (interactive && sliderRoot) {
      var sliderDragging = false;

      function positionFromClientX(clientX) {
        var rect = sliderRoot.getBoundingClientRect();
        var fraction = (clientX - rect.left) / rect.width;
        fraction = Math.max(0, Math.min(1, fraction));
        return Math.round(fraction * (TOTAL - 1)) + 1;
      }

      function startSliderDrag(clientX) {
        if (!stage1Ready) return;
        sliderDragging = true;
        cancelIntro();
        cancelInertia();
        hideHint();
        showIndicator();
        setFrame(positionFromClientX(clientX));
      }

      function moveSliderDrag(clientX) {
        if (!sliderDragging) return;
        setFrame(positionFromClientX(clientX));
      }

      function endSliderDrag() {
        if (!sliderDragging) return;
        sliderDragging = false;
        scheduleHideIndicator();
      }

      // stopPropagation everywhere below: the slider lives INSIDE the same
      // element that carries the image-drag listeners (container), so
      // without it every slider gesture would also arm/advance the image
      // drag via bubbling — both paths would fight over setFrame() on the
      // same move event otherwise.

      sliderRoot.addEventListener('mousedown', function (e) {
        e.preventDefault();
        e.stopPropagation();
        startSliderDrag(e.clientX);
      });

      window.addEventListener('mousemove', function (e) {
        moveSliderDrag(e.clientX);
      });

      window.addEventListener('mouseup', endSliderDrag);

      var sliderTouchStartX     = null;
      var sliderTouchStartY     = null;
      var sliderIntentDetected  = false;

      sliderRoot.addEventListener('touchstart', function (e) {
        e.stopPropagation();
        sliderTouchStartX = e.touches[0].clientX;
        sliderTouchStartY = e.touches[0].clientY;
        sliderIntentDetected = false;
      }, { passive: true });

      sliderRoot.addEventListener('touchmove', function (e) {
        if (sliderTouchStartX === null) return;
        var x = e.touches[0].clientX;
        var y = e.touches[0].clientY;

        if (!sliderIntentDetected) {
          var dx = x - sliderTouchStartX;
          var dy = y - sliderTouchStartY;
          // Vertical intent — leave it alone so the page keeps scrolling.
          if (Math.abs(dx) <= Math.abs(dy) * 1.5) return;
          sliderIntentDetected = true;
          startSliderDrag(x);
        }

        e.preventDefault();
        e.stopPropagation();
        moveSliderDrag(x);
      }, { passive: false });

      sliderRoot.addEventListener('touchend', function (e) {
        e.stopPropagation();
        sliderTouchStartX = null;
        sliderTouchStartY = null;
        endSliderDrag();
      });

      sliderRoot.addEventListener('touchcancel', function (e) {
        e.stopPropagation();
        sliderTouchStartX = null;
        sliderTouchStartY = null;
        sliderDragging = false;
      });

      sliderRoot.addEventListener('keydown', function (e) {
        if (!stage1Ready) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          cancelIntro();
          cancelInertia();
          hideHint();
          showIndicator();
          setFrame(Math.max(1, currentFrame - 1));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          cancelIntro();
          cancelInertia();
          hideHint();
          showIndicator();
          setFrame(Math.min(TOTAL, currentFrame + 1));
        }
      });

      sliderRoot.addEventListener('focus', function () {
        indicatorFocused = true;
        showIndicator();
      });

      sliderRoot.addEventListener('blur', function () {
        indicatorFocused = false;
        scheduleHideIndicator();
      });

      syncSlider();
    }
  }

  document.querySelectorAll('[data-bs-bag-360]').forEach(init);
})();
